import { createSingletonLogger } from "./singleton-logger.js";
import { types } from "../utils/types.js";

class Validation {
  // Component identity for logging
  static componentName = "validation";
  
  #logger;
  
  constructor() {
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);
  }
  
  // validates property value by its descriptor
  validateProperty(value, propertyDescriptor, arrayItem = false, context = {}) {
    if (!propertyDescriptor) {
      return { isValid: false, message: "" };
    }

    if (value === undefined && propertyDescriptor.optional) {
      return { isValid: true, message: "" };
    }

    if (value === null) {
      const contextInfo = context.uid && context.namespace
        ? ` [UID: ${context.uid}, Namespace: ${context.namespace}]`
        : '';
      return {
        isValid: false,
        message: `The value of the [${propertyDescriptor.name}] property is null${contextInfo}`,
      };
    }

    const runValidationTest = this._getValidationTest(propertyDescriptor, arrayItem, context);
    const validation = runValidationTest(value);

    if (!validation.isValid && validation.message) {
      // Add new logging
      this.#logger.error(validation.message);
    }

    return validation;
  }

  // returns validation function accordingly to its type
  _getValidationTest(descriptor, arrayItem = false, context = {}) {
    const type = arrayItem ? descriptor.valueType : descriptor.type;
    const contextInfo = context.uid && context.namespace 
      ? ` [UID: ${context.uid}, Namespace: ${context.namespace}]`
      : '';

    if (type === "float") {
      return (value) => {
        if (value !== Number(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not a float${contextInfo}`,
          };
        }

        const isInBounds = inBounds(value, descriptor.min, descriptor.max);
        if (!isInBounds) {
          return {
            isValid: false,
            message: `The float [${value}] of the [${descriptor.name}] property is out of the bounds [${descriptor.min}; ${descriptor.max}]${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "int") {
      return (value) => {
        // TODO min max should not have to be defined
        if (!types.isInt(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not an integer${contextInfo}`,
          };
        }

        const isInBounds = inBounds(value, descriptor.min, descriptor.max);
        if (!isInBounds) {
          return {
            isValid: false,
            message: `The integer [${value}] of the [${descriptor.name}] property is out of the bounds [${descriptor.min}; ${descriptor.max}]${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "enum") {
      return (value) => {
        if (!types.isString(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not a string${contextInfo}`,
          };
        }

        if (descriptor.values && !descriptor.values.includes(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not included in the enum [${descriptor.values.join(", ")}]${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "object") {
      return (value) => {
        if (!types.isObject(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not an object${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "color") {
      return (value) => {
        if (!types.isString(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not a valid color string${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "string" || type === "expression") {
      return (value) => {
        if (!types.isString(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not a string${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "boolean") {
      return (value) => {
        if (!types.isBoolean(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not a boolean${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    if (type === "array") {
      return (value) => {
        if (!types.isArray(value)) {
          return {
            isValid: false,
            message: `The value [${value}] of the [${descriptor.name}] property is not an array${contextInfo}`,
          };
        }

        return {
          isValid: true,
          message: "",
        };
      };
    }

    return (value) => {
      if (value === undefined) {
        return {
          isValid: false,
          message: `The value of the [${descriptor.name}] is undefined. If you want your property to be undefined, mark it as optional${contextInfo}`,
        };
      }

      return { isValid: true, message: "" };
    };
  }
}

function inBounds(value, min, max) {
  const greater = min !== undefined && min !== null ? value >= min : true;
  const less = max !== undefined && max !== null ? value <= max : true;

  return greater && less;
}

const validation = new Validation();
export { Validation, validation };